import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import GitRepositoryForm from '../integrations/GitRepositoryForm';
import useGitRepository from '../../hooks/useGitRepository';

/**
 * Repository Selection Step
 * Component to be shown after task selection in the project creation flow
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {string} props.workflowType - The workflow type (scratch, existing, ms)
 * @param {Function} props.onPrevious - Callback function to go back to task selection
 * @param {Function} props.onNext - Callback function to move to the next step (or complete)
 * @param {Function} props.onSkip - Callback function to skip this step
 * @returns {React.ReactElement} Component
 */
const RepositorySelectionStep = ({ projectId, workflowType, onPrevious, onNext, onSkip }) => {
  const { getRepositoryInfo, loading, error } = useGitRepository();
  
  const [connectedRepo, setConnectedRepo] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Create a reference to track if we've already checked - outside the useEffect
  const hasCheckedRef = React.useRef(false);
  
  // Check if repository is already connected - but only once on component mount
  useEffect(() => {
    if (projectId && !hasCheckedRef.current) {
      // Mark that we're performing the check
      hasCheckedRef.current = true;
      
      // First, check sessionStorage to see if we have cached connection info
      const cachedConnection = sessionStorage.getItem(`git_repo_connection_${projectId}`);
      if (cachedConnection) {
        try {
          const parsedConnection = JSON.parse(cachedConnection);
          if (parsedConnection && parsedConnection.url) {
            console.log('Using cached connection info from sessionStorage');
            setConnectedRepo(parsedConnection);
            setActiveStep(1); // Move to next step if repo already connected
            return; // Exit early, no need to make API call
          }
        } catch (err) {
          console.error('Error parsing cached connection:', err);
          // Clear invalid storage
          sessionStorage.removeItem(`git_repo_connection_${projectId}`);
        }
      }
      
      // Only make API call if we don't have cached connection info
      const checkConnection = async () => {
        try {
          // Check if we already know there's no repository connected
          const cacheKey = `repo_check_${projectId}`;
          const hasChecked = sessionStorage.getItem(cacheKey);
          
          if (hasChecked === 'not_found') {
            console.log('Using cached "not connected" status from sessionStorage');
            setConnectedRepo(null);
            setActiveStep(0);
            return; // Skip API call if we know there's no connection
          }
          
          const result = await getRepositoryInfo(projectId);
          // If repository info exists and has a URL, it's connected
          if (result && result.url) {
            setConnectedRepo(result);
            setActiveStep(1); // Move to next step if repo already connected
            // Store connection data for future use
            sessionStorage.setItem(`git_repo_connection_${projectId}`, JSON.stringify(result));
          } else {
            // Repository is not connected, make sure we're on step 0
            setConnectedRepo(null);
            setActiveStep(0);
            // Mark as checked but not found in sessionStorage
            sessionStorage.setItem(`repo_check_${projectId}`, 'not_found');
          }
        } catch (err) {
          // An unexpected error occurred - log it but don't disrupt user flow
          console.error('Error checking repository connection:', err.message);
          // Reset to initial state to allow repository connection
          setConnectedRepo(null);
          setActiveStep(0);
        }
      };
      
      checkConnection();
    }
  }, [projectId]); // Remove getRepositoryInfo from dependencies to prevent re-runs
  
  // Handle successful repository connection
  const handleRepositoryConnected = (repoInfo) => {
    setConnectedRepo(repoInfo);
    setActiveStep(1);
    
    // Store connection data in sessionStorage to avoid unnecessary API calls
    if (projectId && repoInfo && repoInfo.url) {
      // Store the connection info
      sessionStorage.setItem(`git_repo_connection_${projectId}`, JSON.stringify(repoInfo));
      // Clear any "not found" marker if it was set previously
      sessionStorage.removeItem(`repo_check_${projectId}`);
    }
  };
  
  // Handle next button click
  const handleNext = () => {
    if (activeStep === 1) {
      // Create Claude Code workspace before moving to the next step
      // This could be done here or in the onNext callback
      if (onNext) {
        onNext(connectedRepo);
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };
  
  // Handle back button click
  const handleBack = () => {
    if (activeStep === 0) {
      // Go back to task selection
      if (onPrevious) {
        onPrevious();
      }
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };
  
  // Handle skip button click
  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };
  
  // Steps definition for the stepper
  const steps = [
    'Connect Repository',
    'Verify Connection'
  ];
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '4px',
        p: { xs: 2, sm: 3 },
        width: '100%',
        bgcolor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 200px)',
        maxHeight: 'calc(100vh - 150px)',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Connect Code Repository
        </Typography>
      
        {/* Stepper */}
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      {/* Step content - Main content area */}
      <Box sx={{ 
        flex: 1, 
        mb: 2, 
        pt: 1,
        pb: 3, // Add more bottom padding for spacing from the sticky footer
      }}>
        {activeStep === 0 && (
          <Box>
            <GitRepositoryForm 
              projectId={projectId} 
              onSuccess={handleRepositoryConnected}
            />
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box>
            {connectedRepo ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  Repository connected successfully!
                </Alert>
                
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Connected Repository Details
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                      Repository URL
                    </Typography>
                    <Typography variant="body1">
                      {connectedRepo.url}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                      Branch
                    </Typography>
                    <Typography variant="body1">
                      <Chip 
                        label={connectedRepo.branch} 
                        size="small" 
                        variant="outlined" 
                        sx={{ 
                          backgroundColor: 'rgba(25, 118, 210, 0.08)', 
                          borderColor: 'rgba(25, 118, 210, 0.2)',
                          color: '#1976d2',
                          fontWeight: 500
                        }} 
                      />
                    </Typography>
                  </Box>
                  
                  {connectedRepo.file_count && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                        File Count
                      </Typography>
                      <Typography variant="body1">
                        {connectedRepo.file_count.toLocaleString()} files
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Loading repository information...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      {/* Bottom buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        pt: 3,
        mt: 'auto',
        borderTop: '1px solid', 
        borderColor: 'divider',
        position: 'sticky',
        bottom: 0,
        bgcolor: 'white',
        zIndex: 1000,
        mx: -3,
        px: 3,
      }}>
        {/* Only show back button for workflows other than existing/ms */}
        {workflowType !== 'existing' && workflowType !== 'ms' && (
          <Button
            color="inherit"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            disabled={loading}
            size="large"
          >
            {activeStep === 0 ? 'Back to Tasks' : 'Back'}
          </Button>
        )}
        
        <Box>
          {/* Show skip button for all workflows */}
          {activeStep === 0 && (
            <Button
              variant="outlined"
              onClick={handleSkip}
              sx={{ mr: 2 }}
              disabled={loading}
              size="large"
            >
              Skip This Step
            </Button>
          )}
          
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            disabled={loading || (activeStep === 0 && !connectedRepo)}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {activeStep === steps.length - 1 ? 'Continue' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default RepositorySelectionStep;